import React, { useEffect, useState } from 'react';
import ForgeIcon from './ForgeIcon';
import StatusPill from './StatusPill';

const emptyForm = { name: '', responsible_id: '', member_ids: [] };

function errorDetail(detail) {
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || String(item)).join(' · ');
  }
  if (detail && typeof detail === 'object') return detail.message;
  return detail;
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(errorDetail(data.detail) || 'No se pudo completar la operación.');
  return data;
}

export default function TeamAdmin({ apiUrl, users }) {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    try {
      setGroups(await requestJson(`${apiUrl}/api/admin/readiness/groups`));
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  useEffect(() => { load(); }, [apiUrl]);

  const toggleMember = (userId) => setForm((prev) => ({
    ...prev,
    member_ids: prev.member_ids.includes(userId)
      ? prev.member_ids.filter((id) => id !== userId)
      : [...prev.member_ids, userId],
  }));

  const edit = (group) => {
    setEditingId(group.id);
    setForm({
      name: group.name,
      responsible_id: group.responsible?.id || '',
      member_ids: group.members.map((member) => member.user_id),
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await requestJson(
        editingId
          ? `${apiUrl}/api/admin/readiness/groups/${editingId}`
          : `${apiUrl}/api/admin/readiness/groups`,
        {
          method: editingId ? 'PUT' : 'POST',
          body: JSON.stringify({
            ...form,
            responsible_id: Number(form.responsible_id),
          }),
        }
      );
      setForm(emptyForm);
      setEditingId(null);
      setMessage({ type: 'success', text: editingId ? 'Grupo actualizado.' : 'Grupo creado.' });
      await load();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const activeUsers = users.filter((user) => user.is_active !== false);
  return (
    <section className="forge-panel admin-panel team-admin">
      <div className="admin-panel-head">
        <ForgeIcon name="users" />
        <div><h2>Grupos de preparación</h2><p>Responsables, integrantes y acompañamiento educativo.</p></div>
      </div>
      {message && <div className={`admin-message ${message.type}`}>{message.text}</div>}
      <form className="admin-form team-admin-form" onSubmit={submit}>
        <label>Nombre<input required value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} /></label>
        <label>Responsable
          <select required value={form.responsible_id} onChange={(event) => setForm((prev) => ({ ...prev, responsible_id: event.target.value }))}>
            <option value="">Selecciona…</option>
            {activeUsers.map((user) => <option key={user.id} value={user.id}>@{user.username} · {user.roles.join(', ')}</option>)}
          </select>
        </label>
        <fieldset className="team-member-picker">
          <legend>Integrantes</legend>
          {activeUsers.map((user) => (
            <label key={user.id}>
              <input type="checkbox" checked={form.member_ids.includes(user.id)} onChange={() => toggleMember(user.id)} />
              <span>@{user.username}</span><small>{user.roles.join(', ')}</small>
            </label>
          ))}
        </fieldset>
        <div className="team-admin-actions">
          <button className="forge-button primary" type="submit" disabled={busy || !form.responsible_id}>{busy ? 'Guardando…' : editingId ? 'Actualizar grupo' : 'Crear grupo'}</button>
          {editingId && <button className="forge-button" type="button" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancelar</button>}
        </div>
      </form>
      <div className="team-admin-list">
        {groups.map((group) => (
          <article key={group.id}>
            <div><strong>{group.name}</strong><span>@{group.responsible?.username || 'sin responsable'} · {group.member_count} integrantes</span></div>
            <StatusPill color={group.status === 'active' ? 'green' : 'amber'}>{group.status === 'active' ? 'Activo' : 'Suspendido'}</StatusPill>
            <button type="button" className="forge-button" onClick={() => edit(group)}>Editar</button>
          </article>
        ))}
        {!groups.length && <div className="forge-empty">Aún no hay grupos.</div>}
      </div>
    </section>
  );
}
